"use client";

import Link from "next/link";
import { useMemo } from "react";
import { BadgePercent, CalendarPlus, ChevronDown, Search } from "lucide-react";
import { FilterSelect, ViewSwitcher } from "@/components/local-buzz/LocalBuzzControls";
import { LocalBuzzBusinessCard, LocalBuzzDealCard, LocalBuzzEventCard } from "@/components/local-buzz/LocalBuzzCards";
import { BusinessTable, DealTable, DealWeekCalendar, EventCalendar, EventTable } from "@/components/local-buzz/LocalBuzzListViews";
import { LocalBuzzBusinessCta, LocalBuzzFooter, LocalBuzzShell } from "@/components/local-buzz/LocalBuzzShell";
import type { BuzzBusiness, BuzzDeal, BuzzEvent } from "@/components/local-buzz/types";
import {
  createLocalBuzzDealsStore,
  createLocalBuzzDirectoryStore,
  createLocalBuzzEventsStore,
  type LocalBuzzDirectoryView
} from "@/stores/discovery-stores";
import { useScopedStore } from "@/stores/scoped-store";

type ListingHeroCopy = {
  label: string;
  title: string;
  script: string;
  copy: string;
  ctaHref?: string;
  ctaLabel?: string;
};

type SubmissionCta = {
  href: string;
  title: string;
  copy: string;
  buttonLabel: string;
  type: "event" | "deal";
};

export function LocalBuzzEventsExplorer({
  events,
  basePath = "/events",
  storeKey = "local-buzz-events:/events",
  cardLabel = "Event",
  itemLabel = "event",
  hero,
  submissionCta
}: {
  events: BuzzEvent[];
  basePath?: string;
  storeKey?: string;
  cardLabel?: string;
  itemLabel?: string;
  hero?: ListingHeroCopy;
  submissionCta?: SubmissionCta;
}) {
  const { view, suburb, category, visibleCount, setView, setSuburb, setCategory, showMore, reset } = useScopedStore(
    storeKey,
    createLocalBuzzEventsStore,
    (state) => state
  );
  const heroCopy = hero ?? {
    label: "Events",
    title: "What's on across the",
    script: "neighbourhood.",
    copy: "Workshops, live music, family days and pop-ups - filter by suburb or category, switch to table or calendar view."
  };
  const itemPlural = `${itemLabel}s`;
  const suburbs = useMemo(() => Array.from(new Set(events.map((event) => event.suburb))).sort(), [events]);
  const categories = useMemo(() => Array.from(new Set(events.map((event) => event.category))).sort(), [events]);
  const filtered = events.filter((event) => (suburb === "all" || event.suburb === suburb) && (category === "all" || event.category === category));
  const visible = filtered.slice(0, visibleCount);
  return (
    <LocalBuzzShell>
      <ListingHero {...heroCopy} />
      <section className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect label="Suburb" value={suburb} onChange={setSuburb} options={[{ value: "all", label: "All suburbs" }, ...suburbs.map((value) => ({ value, label: value }))]} />
            <FilterSelect label="Category" value={category} onChange={setCategory} options={[{ value: "all", label: "All categories" }, ...categories.map((value) => ({ value, label: value }))]} />
            <button type="button" onClick={reset} className="px-2 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground hover:text-brand">Reset</button>
            <ResultCount visible={visible.length} total={filtered.length} label="result" />
          </div>
          <ViewSwitcher view={view} onChange={setView} />
        </div>
        {submissionCta ? <SubmissionCtaCard cta={submissionCta} /> : null}
        <div className="mt-8">{filtered.length === 0 ? <Empty label={`No ${itemPlural} match these filters.`} /> : view === "cards" ? <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{visible.map((event) => <LocalBuzzEventCard key={event.id} event={event} basePath={basePath} label={cardLabel} />)}</div> : view === "table" ? <EventTable events={visible} basePath={basePath} /> : <EventCalendar events={visible} basePath={basePath} itemLabel={itemLabel} />}</div>
        <ShowMoreButton visible={visible.length} total={filtered.length} onClick={showMore} />
      </section>
      <LocalBuzzBusinessCta />
      <LocalBuzzFooter />
    </LocalBuzzShell>
  );
}

export function LocalBuzzMarketsExplorer({ markets }: { markets: BuzzEvent[] }) {
  return (
    <LocalBuzzEventsExplorer
      events={markets}
      basePath="/markets"
      storeKey="local-buzz-markets:/markets"
      cardLabel="Market"
      itemLabel="market"
      hero={{
        label: "Markets",
        title: "Weekend markets &",
        script: "local pop-ups.",
        copy: "Farmers markets, makers stalls and night-market sessions - filter by suburb or market type, then switch between cards, table and calendar."
      }}
    />
  );
}

export function LocalBuzzDealsExplorer({ deals, submissionCta }: { deals: BuzzDeal[]; submissionCta?: SubmissionCta }) {
  const { view, suburb, type, visibleCount, setView, setSuburb, setType, showMore, reset } = useScopedStore(
    "local-buzz-deals:/deals",
    createLocalBuzzDealsStore,
    (state) => state
  );
  const suburbs = useMemo(() => Array.from(new Set(deals.map((deal) => deal.suburb))).sort(), [deals]);
  const filtered = deals.filter((deal) => (suburb === "all" || deal.suburb === suburb) && (type === "all" || deal.type === type));
  const visible = filtered.slice(0, visibleCount);
  return (
    <LocalBuzzShell>
      <ListingHero label="Deals" title="Intro offers &" script="lunch specials." copy="Honest deals from venues we know - filter, switch to table view, or see who's running specials each day." />
      <section className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect label="Suburb" value={suburb} onChange={setSuburb} options={[{ value: "all", label: "All suburbs" }, ...suburbs.map((value) => ({ value, label: value }))]} />
            <FilterSelect label="Type" value={type} onChange={setType} options={[{ value: "all", label: "All offers" }, { value: "deal", label: "Deals" }, { value: "intro-offer", label: "Intro offers" }]} />
            <button type="button" onClick={reset} className="px-2 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground hover:text-brand">Reset</button>
            <ResultCount visible={visible.length} total={filtered.length} label="result" />
          </div>
          <ViewSwitcher view={view} onChange={setView} />
        </div>
        {submissionCta ? <SubmissionCtaCard cta={submissionCta} /> : null}
        <div className="mt-8">{filtered.length === 0 ? <Empty label="No deals match these filters." /> : view === "cards" ? <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{visible.map((deal) => <LocalBuzzDealCard key={deal.id} deal={deal} />)}</div> : view === "table" ? <DealTable deals={visible} /> : <DealWeekCalendar deals={visible} />}</div>
        <ShowMoreButton visible={visible.length} total={filtered.length} onClick={showMore} />
      </section>
      <LocalBuzzBusinessCta />
      <LocalBuzzFooter />
    </LocalBuzzShell>
  );
}

export function LocalBuzzDirectoryExplorer({ businesses }: { businesses: BuzzBusiness[] }) {
  const { view, suburb, category, query, visibleCount, setView, setSuburb, setCategory, setQuery, showMore, reset } = useScopedStore(
    "local-buzz-directory:/directory",
    createLocalBuzzDirectoryStore,
    (state) => state
  );
  const suburbs = useMemo(() => Array.from(new Set(businesses.map((business) => business.suburb))).sort(), [businesses]);
  const categories = useMemo(() => Array.from(new Set(businesses.map((business) => business.category))).sort(), [businesses]);
  const filtered = businesses.filter((business) => {
    if (suburb !== "all" && business.suburb !== suburb) return false;
    if (category !== "all" && business.category !== category) return false;
    const haystack = [business.name, business.description, business.category, business.subcategory, business.suburb].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });
  const visible = filtered.slice(0, visibleCount);
  return (
    <LocalBuzzShell>
      <ListingHero label="Directory" title="The local" script="business directory." copy="Browse every merchant we feature - filter by suburb and category, search by name, or switch between table and card views." ctaHref="/business/listing" ctaLabel="List your business" />
      <section className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect label="Suburb" value={suburb} onChange={setSuburb} options={[{ value: "all", label: "All suburbs" }, ...suburbs.map((value) => ({ value, label: value }))]} />
            <FilterSelect label="Category" value={category} onChange={setCategory} options={[{ value: "all", label: "All categories" }, ...categories.map((value) => ({ value, label: value }))]} />
            <label className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search merchants..." className="rounded-full border border-border bg-card py-2 pl-8 pr-4 text-[12px] font-medium outline-none placeholder:text-muted-foreground focus:border-brand" />
            </label>
            <button type="button" onClick={reset} className="px-2 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground hover:text-brand">Reset</button>
            <ResultCount visible={visible.length} total={filtered.length} label="merchant" />
          </div>
          <div className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-card p-1">
            {[
              ["table", "Table"],
              ["cards-image", "Cards"],
              ["cards-text", "Compact"]
            ].map(([id, label]) => <button key={id} type="button" onClick={() => setView(id as LocalBuzzDirectoryView)} className={`rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] transition ${view === id ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>)}
          </div>
        </div>
        <div className="mt-8">{filtered.length === 0 ? <Empty label="No merchants match these filters." /> : view === "table" ? <BusinessTable businesses={visible} /> : <div className={`grid gap-5 sm:grid-cols-2 ${view === "cards-text" ? "lg:grid-cols-3" : "lg:grid-cols-3"}`}>{visible.map((business) => <LocalBuzzBusinessCard key={business.id} business={business} />)}</div>}</div>
        <ShowMoreButton visible={visible.length} total={filtered.length} onClick={showMore} />
      </section>
      <LocalBuzzFooter />
    </LocalBuzzShell>
  );
}

function ListingHero({ label, title, script, copy, ctaHref, ctaLabel }: { label: string; title: string; script: string; copy: string; ctaHref?: string; ctaLabel?: string }) {
  return (
    <section className="bg-blush text-blush-foreground">
      <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 sm:py-14">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand">{label}</div>
        <h1 className="mt-2 max-w-3xl font-display text-4xl font-black leading-[0.95] sm:text-5xl lg:text-6xl">{title} <span className="text-brand">{script}</span></h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-blush-foreground/80 sm:text-base">{copy}</p>
        {ctaHref && ctaLabel ? <a href={ctaHref} className="mt-5 inline-flex rounded-full bg-brand px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.16em] text-white shadow-sm transition hover:opacity-90">{ctaLabel}</a> : null}
      </div>
    </section>
  );
}

function ResultCount({ visible, total, label }: { visible: number; total: number; label: string }) {
  const plural = total === 1 ? label : `${label}s`;
  const text = total === 0 || visible >= total ? `${total} ${plural}` : `${visible} of ${total} ${plural}`;
  return <span className="ml-1 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{text}</span>;
}

function ShowMoreButton({ visible, total, onClick }: { visible: number; total: number; onClick: () => void }) {
  if (visible >= total) return null;

  return (
    <div className="mt-8 flex justify-center">
      <button type="button" onClick={onClick} className="nv-button-secondary inline-flex min-h-11 items-center gap-2 px-5 py-3 text-xs">
        Show more
        <ChevronDown className="h-4 w-4" />
      </button>
    </div>
  );
}

function SubmissionCtaCard({ cta }: { cta: SubmissionCta }) {
  const Icon = cta.type === "event" ? CalendarPlus : BadgePercent;
  return (
    <div className="mt-6 flex flex-col gap-4 rounded-lg border border-border bg-card p-5 shadow-soft sm:flex-row sm:items-center sm:justify-between">
      <div className="flex gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-lg font-black tracking-tight">{cta.title}</h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">{cta.copy}</p>
        </div>
      </div>
      <Link href={cta.href} className="nv-button inline-flex min-h-11 shrink-0 items-center justify-center px-5 py-3 text-xs">
        {cta.buttonLabel}
      </Link>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="rounded-xl border border-dashed border-border bg-card/40 py-16 text-center text-sm text-muted-foreground">{label}</div>;
}
